import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader, EmptyState } from "@/components/Common";
import { toast } from "sonner";
import {
  Plus, Search, Trash2, Pencil, ImagePlus, ImageOff, Upload, X,
} from "lucide-react";

const EMPTY_FORM = {
  name: "", description: "", category: "", sku: "",
  price: "", currency: "UGX", stock_quantity: "", branch: "",
  is_published: true, image_file_id: null,
};

// Client-side validation
const MAX_IMG_MB = 5;
const IMG_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

async function uploadImageFile(file) {
  if (!IMG_TYPES.includes(file.type)) {
    throw new Error("Only JPG, PNG, WEBP, or GIF images are allowed");
  }
  if (file.size > MAX_IMG_MB * 1024 * 1024) {
    throw new Error(`Image is larger than ${MAX_IMG_MB} MB`);
  }
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/files/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.id;
}

// ---- Image picker component (reused for new + edit) ----
function ImagePicker({ imageFileId, onChange, testidPrefix = "image" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const preview = imageFileId ? fileUrl(imageFileId) : null;

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file) return;
    setUploading(true);
    try {
      const id = await uploadImageFile(file);
      onChange(id);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={IMG_TYPES.join(",")}
        onChange={onFile}
        className="hidden"
        data-testid={`${testidPrefix}-file-input`}
      />
      {preview ? (
        <div className="relative rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
          <img
            src={preview}
            alt="product"
            className="w-full aspect-square object-cover"
            data-testid={`${testidPrefix}-preview`}
          />
          <div className="absolute top-2 right-2 flex gap-1.5">
            <Button
              type="button" size="sm" variant="secondary"
              onClick={pick} disabled={uploading}
              className="h-8 gap-1 shadow"
              data-testid={`${testidPrefix}-change`}>
              <Upload size={12} /> {uploading ? "Uploading…" : "Change"}
            </Button>
            <Button
              type="button" size="sm" variant="destructive"
              onClick={() => onChange(null)}
              className="h-8 gap-1 shadow"
              data-testid={`${testidPrefix}-remove`}>
              <X size={12} /> Remove
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          data-testid={`${testidPrefix}-upload`}
          className="w-full aspect-square rounded-lg border-2 border-dashed border-gray-300
                     bg-gray-50 hover:bg-emerald-50 hover:border-emerald-500 transition-colors
                     flex flex-col items-center justify-center text-gray-500 hover:text-emerald-700">
          <ImagePlus size={28} />
          <span className="text-sm mt-2 font-medium">
            {uploading ? "Uploading…" : "Browse an image"}
          </span>
          <span className="text-[11px] text-gray-400 mt-1">JPG, PNG, WEBP · up to {MAX_IMG_MB} MB</span>
        </button>
      )}
    </div>
  );
}

// ---- Product form dialog (create + edit) ----
function ProductFormDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    setForm(initial || EMPTY_FORM);
  }, [initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || "",
        category: form.category || "",
        sku: form.sku || "",
        price: Number(form.price) || 0,
        currency: form.currency || "UGX",
        stock_quantity: form.stock_quantity !== "" ? Number(form.stock_quantity) : null,
        branch: form.branch || "",
        is_published: form.is_published !== false,
        image_file_id: form.image_file_id || null,
      };
      if (isEdit) {
        await api.patch(`/products/${initial.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEdit ? "Edit product" : "Add product"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-5">
          <div className="md:col-span-2">
            <Label className="text-xs mb-1.5 block">Product image</Label>
            <ImagePicker
              imageFileId={form.image_file_id}
              onChange={(id) => set("image_file_id", id)}
              testidPrefix="product-image"
            />
          </div>

          <div className="md:col-span-3 space-y-3">
            <div>
              <Label className="text-xs">Product name *</Label>
              <Input required value={form.name}
                onChange={(e) => set("name", e.target.value)}
                data-testid="product-name-input" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={3} value={form.description}
                onChange={(e) => set("description", e.target.value)}
                data-testid="product-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Input value={form.category}
                  onChange={(e) => set("category", e.target.value)}
                  placeholder="Groceries, Bakery…"
                  data-testid="product-category-input" />
              </div>
              <div>
                <Label className="text-xs">SKU / barcode</Label>
                <Input value={form.sku}
                  onChange={(e) => set("sku", e.target.value)}
                  data-testid="product-sku-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Price *</Label>
                <Input type="number" required min={0}
                  value={form.price}
                  onChange={(e) => set("price", e.target.value)}
                  data-testid="product-price-input" />
              </div>
              <div>
                <Label className="text-xs">Currency</Label>
                <Input value={form.currency}
                  onChange={(e) => set("currency", e.target.value.toUpperCase())} />
              </div>
              <div>
                <Label className="text-xs">Stock</Label>
                <Input type="number" min={0}
                  value={form.stock_quantity}
                  onChange={(e) => set("stock_quantity", e.target.value)}
                  data-testid="product-stock-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Source branch</Label>
              <Input value={form.branch}
                onChange={(e) => set("branch", e.target.value)}
                placeholder="Main Abrah" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium">Published on storefront</div>
                <div className="text-[11px] text-gray-500">Turn off to hide without deleting.</div>
              </div>
              <Switch
                checked={form.is_published !== false}
                onCheckedChange={(v) => set("is_published", v)}
                data-testid="product-published-switch" />
            </div>
          </div>
        </form>
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !form.name?.trim() || !form.price}
            className="bg-emerald-900 hover:bg-emerald-950 text-white"
            data-testid="product-submit-button">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Add product")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Page ----
export default function Products() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // product being edited, or {} for new, or null for closed

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const togglePublished = async (p) => {
    try {
      await api.patch(`/products/${p.id}`, { is_published: p.is_published === false });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    }
  };

  const filtered = items.filter((p) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [p.name, p.category, p.sku, p.branch].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div>
      <PageHeader
        eyebrow="E-commerce"
        title="Product Catalogue"
        subtitle="Products shown on the public storefront. Upload an image, edit any time, and toggle Publish to hide without deleting."
        actions={
          <Button
            className="bg-emerald-900 hover:bg-emerald-950 text-white"
            onClick={() => setEditing({})}
            data-testid="product-new-button">
            <Plus size={16} className="mr-1.5" /> Add product
          </Button>
        }
      />

      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9"
            data-testid="product-search-input" />
        </div>
        <span className="text-xs text-gray-500">{filtered.length} product(s)</span>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-md">
          <EmptyState title="No products yet" hint="Click 'Add product' to upload your first item." />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          data-testid="product-grid">
          {filtered.map((p) => (
            <div key={p.id}
              className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow overflow-hidden group"
              data-testid={`product-card-${p.id}`}>
              <div className="aspect-square bg-gray-100 relative">
                {p.image_file_id ? (
                  <img src={fileUrl(p.image_file_id)} alt={p.name}
                    className="object-cover w-full h-full" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-300">
                    <ImageOff size={30} />
                    <span className="text-[11px] mt-1">No image</span>
                  </div>
                )}
                <div className="absolute top-2 left-2 flex gap-1">
                  <Badge className={p.is_published === false
                    ? "bg-gray-500 hover:bg-gray-500" : "bg-emerald-700 hover:bg-emerald-700"}>
                    {p.is_published === false ? "Hidden" : "Live"}
                  </Badge>
                </div>
                <div className="absolute inset-x-2 bottom-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1.5">
                  <Button
                    size="sm" variant="secondary" className="h-8 flex-1 gap-1 shadow"
                    onClick={() => setEditing(p)}
                    data-testid={`product-edit-${p.id}`}>
                    <Pencil size={12} /> Edit
                  </Button>
                  <Button
                    size="sm" variant="destructive" className="h-8 gap-1 shadow"
                    onClick={() => remove(p)}
                    data-testid={`product-delete-${p.id}`}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
              <div className="p-3">
                <div className="font-semibold text-sm text-gray-900 truncate">{p.name}</div>
                <div className="text-xs text-gray-500 truncate">{p.category || "—"}</div>
                <div className="flex items-baseline justify-between mt-2">
                  <div className="font-display font-bold text-emerald-800">
                    {p.currency || "UGX"} {Number(p.price || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500">
                    {p.stock_quantity != null ? `${p.stock_quantity} in stock` : "—"}
                  </div>
                </div>
                <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-[11px] text-gray-500">Published</span>
                  <Switch
                    checked={p.is_published !== false}
                    onCheckedChange={() => togglePublished(p)}
                    data-testid={`product-published-${p.id}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ProductFormDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        initial={editing?.id ? editing : null}
        onSaved={load}
      />
    </div>
  );
}
